/**
 * BatchWeeklyUpdateModal — the governed review gate for firing the Weekly
 * Customer Update at EVERY account in one action (bold bet #1).
 *
 * The pure engine (`crm/weeklyUpdate.ts` → `buildBatchWeeklyUpdates`) has
 * already assembled one honest digest per account and resolved each
 * recipient; THIS component is where the human reviews the whole batch:
 *
 *   - Every sendable account shows its resolved recipient, a per-row
 *     include/exclude toggle, and an expandable preview of the exact
 *     plain-text body that would go out.
 *   - Accounts with no resolvable email are listed as SKIPPED with the
 *     honest reason ("No contact email on file") — never a guessed address.
 *   - "Send N updates" reuses the single-send contract per row: queue the
 *     real email via `sendTransactionalEmail`, and ONLY THEN append the
 *     hash-chained Decision Ledger entry (`logActivity`, kind
 *     'weekly_update'). A ledger failure never re-reports a queued send.
 *   - The action is labeled NOT reversible, because it isn't: N emails
 *     leave the building.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Mail, Send, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useToast } from './ToastProvider';
import { sendTransactionalEmail } from '../lib/email';
import { logActivity } from '../lib/db/activity';
import { renderWeeklyUpdateEmail, type BatchWeeklyUpdateEntry } from '../lib/crm/weeklyUpdate';

interface Props {
  /** Pre-assembled by buildBatchWeeklyUpdates — one entry per account. */
  entries: BatchWeeklyUpdateEntry[];
  onClose: () => void;
}

export function BatchWeeklyUpdateModal({ entries, onClose }: Props) {
  const { push } = useToast();
  const sendable = useMemo(() => entries.filter((e) => e.recipient !== null), [entries]);
  const skipped = useMemo(() => entries.filter((e) => e.recipient === null), [entries]);

  // Every sendable account starts INCLUDED — the toggle is the opt-out.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  const included = sendable.filter((e) => !excluded.has(e.account.id));
  const canSend = included.length > 0 && !sending;

  function toggle(accountId: string) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }

  async function onSendAll() {
    if (included.length === 0) return;
    setSending(true);
    let sent = 0;
    const failures: string[] = [];
    let ledgerMisses = 0;

    // Sequential on purpose: each send is queued and sealed before the
    // next, so a mid-batch failure leaves an exact, honest count behind.
    for (const entry of included) {
      const recipient = entry.recipient;
      if (!recipient) continue; // type guard — sendable rows always have one
      try {
        const rendered = renderWeeklyUpdateEmail(entry.digest);
        await sendTransactionalEmail({
          to: recipient.email,
          subject: rendered.subject,
          textContent: rendered.textContent,
          htmlContent: rendered.htmlContent,
        });
        sent++;
        // Seal the send in the Decision Ledger — ONLY after the queue
        // succeeded. Best-effort per the ledger's own contract.
        try {
          await logActivity({
            event: 'executed',
            source: 'chat',
            kind: 'weekly_update',
            summary: `Weekly update sent to ${entry.account.name} (${recipient.email})`,
            subjectId: entry.account.id,
          });
        } catch {
          ledgerMisses++;
        }
      } catch {
        failures.push(entry.account.name);
      }
    }

    setSending(false);
    if (failures.length > 0) {
      push({
        kind: sent > 0 ? 'warning' : 'error',
        text: `Sent ${sent} of ${included.length} weekly updates`,
        detail: `Failed for: ${failures.join(', ')} — nothing was queued for those accounts.`,
      });
    } else {
      push({
        kind: 'success',
        text: `Sent ${sent} weekly update${sent === 1 ? '' : 's'}`,
        detail: 'Each send is sealed in the Decision Ledger (Admin → Audit).',
      });
    }
    if (ledgerMisses > 0) {
      push({
        kind: 'warning',
        text: `${ledgerMisses} ledger ${ledgerMisses === 1 ? 'entry' : 'entries'} failed`,
        detail: 'The emails queued, but some Decision Ledger appends did not complete.',
      });
    }
    if (sent > 0 || failures.length === 0) onClose();
  }

  return createPortal(
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        aria-labelledby="batch-weekly-update-title"
        tabIndex={-1}
        style={{ width: 'min(680px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            <Mail size={18} style={{ color: C.magentaSoft, marginRight: 6 }} />
            <div style={{ flex: 1 }}>
              <span className="t-eyebrow">Weekly customer updates — batch</span>
              <h2 id="batch-weekly-update-title" className="t-section-title" style={{ margin: '4px 0 0' }}>
                Review before {sendable.length === 1 ? 'it sends' : 'they send'} — {sendable.length}{' '}
                sendable, {skipped.length} skipped
              </h2>
            </div>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
              disabled={sending}
            >
              <X size={13} />
            </button>
          </header>

          <div style={{ display: 'grid', gap: 14, padding: '12px 0 0' }}>
            {/* Sendable rows — include/exclude + preview per account. */}
            {sendable.length === 0 ? (
              <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.orange }}>
                <AlertTriangle size={13} /> No account has a resolvable contact email — nothing can
                send.
              </div>
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'grid',
                  gap: 8,
                  maxHeight: '46vh',
                  overflowY: 'auto',
                }}
              >
                {sendable.map((entry) => {
                  const recipient = entry.recipient!;
                  const isIncluded = !excluded.has(entry.account.id);
                  const isOpen = previewing === entry.account.id;
                  const preview = isOpen ? renderWeeklyUpdateEmail(entry.digest) : null;
                  return (
                    <li
                      key={entry.account.id}
                      className="glass-row"
                      style={{ padding: '10px 12px', opacity: isIncluded ? 1 : 0.55 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={isIncluded}
                          onChange={() => toggle(entry.account.id)}
                          disabled={sending}
                          aria-label={`Include ${entry.account.name} in the batch send`}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            className="t-card-title"
                            style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {entry.account.name}
                          </span>
                          <span className="t-meta" style={{ display: 'block', marginTop: 2 }}>
                            To {recipient.name} &lt;{recipient.email}&gt;
                          </span>
                        </span>
                        <button
                          type="button"
                          className="glass-btn"
                          onClick={() => setPreviewing(isOpen ? null : entry.account.id)}
                          disabled={sending}
                          aria-expanded={isOpen}
                          title="Preview the exact plain-text body that would send"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}
                        >
                          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Preview
                        </button>
                      </div>
                      {preview && (
                        <pre
                          className="t-meta"
                          style={{
                            margin: '10px 0 0',
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            lineHeight: 1.45,
                            maxHeight: 220,
                            overflowY: 'auto',
                          }}
                        >
                          {`Subject: ${preview.subject}\n\n${preview.textContent}`}
                        </pre>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Skipped rows — honest reason, never a guessed address. */}
            {skipped.length > 0 && (
              <div>
                <div className="t-eyebrow" style={{ marginBottom: 6 }}>
                  Skipped — cannot send
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                  {skipped.map((entry) => (
                    <li
                      key={entry.account.id}
                      className="t-meta"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.orange }}
                    >
                      <AlertTriangle size={12} aria-hidden="true" />
                      <span>
                        {entry.account.name} — {entry.skipReason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Honest irreversibility label. */}
            <div
              className="t-meta"
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.orange }}
            >
              <AlertTriangle size={13} aria-hidden="true" />
              <span>
                Not reversible — sending queues {included.length === 1 ? 'a real email' : `${included.length} real emails`}{' '}
                to customers. Every send is stamped into the Decision Ledger (Admin → Audit).
              </span>
            </div>
          </div>

          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              paddingTop: 14,
              marginTop: 4,
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <button type="button" className="glass-btn" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={() => void onSendAll()}
              disabled={!canSend}
              title={
                included.length > 0
                  ? 'Queue every included email and seal each in the Decision Ledger'
                  : 'Include at least one account to send'
              }
            >
              <Send size={13} />{' '}
              {sending
                ? 'Sending…'
                : `Send ${included.length} update${included.length === 1 ? '' : 's'}`}
            </button>
          </footer>
        </GlassPanel>
      </div>
    </div>,
    document.body,
  );
}
